import { useMemo, useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Button, Col, Container } from 'react-bootstrap';
import BaseTable from 'components/Tables/BaseTable';
import BaseAccordion from 'components/Accordions/BaseAccordion';
import SearchBar from 'components/SearchBar/SearchBar';
import ActionButtons from './components/ActionButtons';
import AddServiceModal from './components/AddServiceModal';
import { EllipsisMenu } from 'components/index';
import { getStatusFromAction, mapServiceRecord, canBeModified } from './components/serviceHelpers';
import { getServices } from 'services/service/service.service';
import { restartService, startService, stopService } from 'services/service/supervisor.service';
import { adminAddService, adminRemoveService } from 'services/service/admin.service';

export default function ServiceManager() {
  const [params] = useSearchParams();
  const search = params.get('search') || '';

  const [showAddModal, setShowAddModal] = useState(false);
  const [rowIdEllipsisOpen, setRowIdEllipsisOpen] = useState({});
  const [mobileRowIdEllipsisOpen, setMobileRowIdEllipsisOpen] = useState({});
  const [services, setServices] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [removedServices, setRemovedServices] = useState([]);
  const [reqTime, setReqTime] = useState(() => new Date());
  const refreshDelayMs = 1000;

  const fetchServices = useCallback(async ({ showSpinner = true } = {}) => {
    try {
      if (showSpinner) setIsLoading(true);
      else setIsRefreshing(true);

      const res = await getServices();
      const apiServices = res?.data?.services || [];
      const validServices = apiServices.filter(
        (service) => service?.pm_id != null && service?.state && service.state !== 'missing',
      );
      const missingServices = apiServices.filter((service) => service?.state === 'missing');

      const mapped = validServices.map(mapServiceRecord);

      setServices(mapped);
      setRemovedServices(missingServices);
    } catch (err) {
      console.error('Failed to load services:', err);
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
      setReqTime(new Date());
    }
  }, []);

  // Initial load
  useEffect(() => {
    fetchServices({ showSpinner: true });
  }, [fetchServices]);

  // Filter by search
  const filteredRows = useMemo(() => {
    if (!search) return services;
    return services.filter((service) => service.name.toLowerCase().includes(search.toLowerCase()));
  }, [search, services]);

  // Action button clicked
  const handleActionChange = (service, action) => {
    setServices((prev) =>
      prev.map((item) =>
        item.id === service.id ? { ...item, status: getStatusFromAction(action) } : item,
      ),
    );

    performServiceAction(service, action);
  };

  const performServiceAction = async (service, action) => {
    if (!service) {
      console.warn('Service not provided for action:', action);
      return;
    }
    // Block Backend service and event
    if (!canBeModified(service)) {
      console.log(`[ServiceManager] Skipping API call for '${service.name}'`);
      return;
    }
    try {
      console.log(`[ServiceManager] Performing '${action}' on ${service.name} via API`);

      if (action === 'start') {
        const res = await startService(service.name);
        console.log('startService response:', res);
      } else if (action === 'stop') {
        const res = await stopService(service.name);
        console.log('stopService response:', res);
      } else if (action === 'restart') {
        const res = await restartService(service.name);
        console.log('restartService response:', res);
      } else {
        console.warn('Unknown action:', action);
      }

      setTimeout(() => {
        fetchServices({ showSpinner: false });
      }, refreshDelayMs);
    } catch (err) {
      console.error(`Error performing '${action}' on '${service.name}' via API:`, err);
    }
  };

  const handleAddService = async (serviceName) => {
    if (!serviceName) return;
    const template = removedServices.find((s) => s.name === serviceName);
    if (!template) return;

    try {
      await adminAddService(serviceName);
      setTimeout(() => {
        fetchServices({ showSpinner: false });
      }, refreshDelayMs);
    } catch (err) {
      console.error(`Error adding service '${serviceName}':`, err);
    }
  };

  const handleRemoveService = async (service) => {
    try {
      await adminRemoveService(service.id);
      setRowIdEllipsisOpen((prev) => {
        const copy = { ...prev };
        delete copy[service.id];
        return copy;
      });
      setMobileRowIdEllipsisOpen((prev) => {
        const copy = { ...prev };
        delete copy[service.id];
        return copy;
      });
      setTimeout(() => {
        fetchServices({ showSpinner: false });
      }, refreshDelayMs);
    } catch (err) {
      console.error('Error removing service:', err);
    }
  };

  const columns = useMemo(
    () => [
      {
        header: 'ID',
        accessorKey: 'id',
        size: 80,
        maxSize: 80,
        Cell: ({ cell }) => <div className="text-white fs-6">{cell.getValue()}</div>,
      },
      {
        header: 'Service',
        accessorKey: 'name',
        size: 240,
        Cell: ({ cell }) => <div className="text-white fs-6">{cell.getValue()}</div>,
      },
      {
        header: 'Status',
        accessorKey: 'status',
        Cell: ({ cell }) => {
          const value = cell.getValue();
          return <span className="px-2 py-1 rounded-3 text-white fw-bold fs-6">{value}</span>;
        },
      },
      {
        header: 'Uptime',
        accessorKey: 'uptime',
        Cell: ({ cell }) => <div className="text-white fs-6">{cell.getValue()}</div>,
      },
      {
        header: 'Restart Count',
        accessorKey: 'restartCount',
        Cell: ({ cell }) => <div className="text-white fs-6">{cell.getValue()}</div>,
      },
      {
        header: 'Unstable Restart',
        accessorKey: 'unstableRestart',
        Cell: ({ cell }) => <div className="text-white fs-6">{cell.getValue()}</div>,
      },
      {
        header: '',
        accessorKey: 'actions',
        enableSorting: false,
        size: 220,
        Cell: ({ row }) => {
          const service = row.original;
          // If system service, disable action buttons
          if (!canBeModified(service)) {
            return <div className="text-white-50 fs-7">Actions disabled for system service</div>;
          }
          return (
            <ActionButtons
              status={service.status}
              onChange={(action) => handleActionChange(service, action)}
            />
          );
        },
      },
      {
        header: '',
        accessorKey: 'menu',
        enableSorting: false,
        size: 80,
        Cell: ({ row }) => {
          const service = row.original;
          const id = service.id;

          if (!canBeModified(service)) {
            return <div className="text-white-50 fs-7"></div>;
          }

          return (
            <EllipsisMenu
              options={[
                {
                  component: (
                    <div className="w-100 d-flex flex-column">
                      <Button
                        className="w-100 px-5 text-center cursor-pointer text-white border border-1 border-black"
                        variant="primary"
                        //TODO: Implement verbose mode
                        onClick={() => alert('Verbose mode not implemented')}
                      >
                        Verbose Mode
                      </Button>
                      <Button
                        className="w-100 px-5 text-center cursor-pointer text-white border border-1 border-black"
                        variant="danger"
                        onClick={() => handleRemoveService(service)}
                      >
                        Remove
                      </Button>
                    </div>
                  ),
                },
              ]}
              open={rowIdEllipsisOpen[id]}
              containerClassname="bg-transparent"
              onOpen={() =>
                setRowIdEllipsisOpen({
                  ...rowIdEllipsisOpen,
                  [id]: true,
                })
              }
              onClose={() =>
                setRowIdEllipsisOpen({
                  ...rowIdEllipsisOpen,
                  [id]: false,
                })
              }
            />
          );
        },
      },
    ],
    [rowIdEllipsisOpen],
  );

  return (
    <Container fluid className="py-3 px-0 h-100">
      <div className="d-flex flex-column gap-3 h-100">
        <div className="bg-base p-3 h-100 d-flex flex-column rounded-3">
          <div className="d-flex flex-column flex-md-row align-items-md-center gap-3 mb-3">
            <Col xs="auto" className="p-0">
              <div className="text-white fw-semibold fs-5">Services</div>
              <div className="text-white fs-7 mt-1">
                Last requested:{' '}
                {reqTime ? reqTime.toLocaleString(undefined, { timeZoneName: 'short' }) : '—'}
              </div>
            </Col>
            <Col className="p-0 d-flex justify-content-end align-items-center gap-2 flex-1">
              <Button disabled={isRefreshing} onClick={() => fetchServices({ showSpinner: false })}>
                {isRefreshing ? 'Refreshing...' : 'Refresh'}
              </Button>
              <SearchBar className="bg-primary-highlight max-w-xs rounded-3" />
            </Col>
          </div>

          <div className="flex-grow-1">
            {filteredRows.length === 0 ? (
              <div className="h-100 d-flex flex-column align-items-center justify-content-center text-white-50">
                <div className="fs-5 text-white">No services to display</div>
              </div>
            ) : (
              <>
                <div className="d-none d-lg-block h-100">
                  <BaseTable
                    columns={columns}
                    rows={filteredRows}
                    expandable
                    columnSizingMode="equal"
                    altRowSettings={{
                      alternate: true,
                      color1: '#222a2d',
                      color2: '#2C373B',
                    }}
                    customStyles={{
                      cell: {
                        borderBottom: '1px solid #56577A',
                        cursor: 'pointer',
                      },
                    }}
                    dropdownMenu={(service) => (
                      <div className="w-100 d-flex flex-column gap-2 text-white">
                        <div className="d-flex gap-3">
                          <span>CPU: {service.cpu}%</span>
                          <span>Memory: {service.memory}</span>
                        </div>
                      </div>
                    )}
                  />
                </div>

                {/* Mobile accordion */}
                <div className="d-block d-lg-none h-100">
                  {filteredRows.map((service, index) => (
                    <div
                      className={index !== filteredRows.length - 1 ? 'mb-3' : ''}
                      key={service.id}
                    >
                      <BaseAccordion
                        key={service.id}
                        bodyBackground="#232B33"
                        header={
                          <div className="d-flex flex-column gap-1 text-white">
                            <div className="fw-semibold">{service.name}</div>
                            <div className="text-secondary fs-7">ID: {service.id}</div>
                            <div className="px-2 py-1 rounded-3 text-white fw-semibold fs-6">
                              {service.status}
                            </div>
                          </div>
                        }
                      >
                        <div className="w-100 d-flex flex-column gap-2 text-white">
                          <div className="d-flex justify-content-between align-items-start">
                            <div>
                              <p className="mb-0">
                                <span className="fw-bold">Uptime:</span> {service.uptime}
                              </p>
                              <p className="mb-0">
                                <span className="fw-bold">Service Created:</span> {service.created}
                              </p>
                              <p className="mb-0">
                                <span className="fw-bold">Restart Count:</span>{' '}
                                {service.restartCount}
                              </p>
                              <p className="mb-0">
                                <span className="fw-bold">Unstable Restart:</span>{' '}
                                {service.unstableRestart}
                              </p>
                            </div>

                            {canBeModified(service) ? (
                              <EllipsisMenu
                                options={[
                                  {
                                    component: (
                                      <div className="w-100 d-flex flex-column">
                                        <Button
                                          className="w-100 px-auto text-center cursor-pointer text-white border border-1 border-black"
                                          variant="primary"
                                          //TODO: Implement verbose mode
                                          onClick={() => alert('Verbose mode not implemented')}
                                        >
                                          Verbose Mode
                                        </Button>
                                        <Button
                                          className="w-100 px-5 text-center cursor-pointer text-white border border-1 border-black"
                                          variant="danger"
                                          onClick={() => handleRemoveService(service)}
                                        >
                                          Remove
                                        </Button>
                                      </div>
                                    ),
                                  },
                                ]}
                                open={mobileRowIdEllipsisOpen[service.id]}
                                containerClassname="bg-transparent"
                                onOpen={() =>
                                  setMobileRowIdEllipsisOpen({
                                    ...mobileRowIdEllipsisOpen,
                                    [service.id]: true,
                                  })
                                }
                                onClose={() =>
                                  setMobileRowIdEllipsisOpen({
                                    ...mobileRowIdEllipsisOpen,
                                    [service.id]: false,
                                  })
                                }
                              />
                            ) : (
                              <div className="text-white-50 fs-7"></div>
                            )}
                          </div>

                          <p className="mb-0">
                            <span className="fw-bold">CPU:</span> {service.cpu}%
                          </p>
                          <p className="mb-0">
                            <span className="fw-bold">Memory:</span> {service.memory}
                          </p>

                          <div className="d-flex gap-2 mt-2">
                            {service.name.includes('FusionBackend') ||
                            service.name.includes('EventService') ||
                            service.name.includes('SupervisorService') ? (
                              <div className="text-white-50 fs-7">
                                Actions disabled for system service
                              </div>
                            ) : (
                              <ActionButtons
                                status={service.status}
                                onChange={(action) => handleActionChange(service, action)}
                              />
                            )}
                          </div>
                        </div>
                      </BaseAccordion>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
          <Col className="d-flex justify-content-end mt-3 px-0">
            <Button variant="primary" className="px-4" onClick={() => setShowAddModal(true)}>
              Add Service
            </Button>
          </Col>
        </div>
      </div>
      <AddServiceModal
        show={showAddModal}
        onClose={() => setShowAddModal(false)}
        onAdd={handleAddService}
        availableServices={removedServices}
      />
    </Container>
  );
}
